// We can define classes and interface here.

import { Asset, CompanyBaseDataWithClients, TagsWithAssetCount } from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType, AssetTypeEnum } from '@thehubz/shared/model';
import { ViewType } from '../constants';

export interface Language {
  id: string;
  name: string;
}

export interface AspectRationFormatTree {
  name?: string;
  id?: string;
  children?: AspectRationFormatTree[];
  selected?: boolean;
  indeterminate?: boolean;
  parent?: AspectRationFormatTree;
  version?: number;
  aspectRatio?: string;
  defaultFileFormat?: string;
  aspectRatioName?: string;
}

export interface NestedTreeNode {
  name?: string;
  id?: string;
  children?: NestedTreeNode[];
  selected?: boolean;
  indeterminate?: boolean;
  parent?: NestedTreeNode;
  expiryDate?: string;
}

export interface ClientsBrandsTree {
  name?: string;
  id?: string;
  children?: ClientsBrandsTree[];
  selected?: boolean;
  indeterminate?: boolean;
  parent?: ClientsBrandsTree;
}

export interface AnnotationStatusEmailTree extends ClientsBrandsTree {
  sharedId?: string;
  children?: AnnotationStatusEmailTree[];
}

export type ColumnType = 'default' | 'additional' | 'shared_task_status';

export enum ColumnKeys {
  defaultColumns = 'defaultColumns',
  additionalColumns = 'additionalColumns',
  sharedTaskStatusColumn = 'sharedTaskStatusColumn',
}

export enum TableColumnType {
  icon,
  date,
  type,
  name,
  link,
  tags,
  html,
  chips,
  array,
  action,
  length,
  expand,
  owners,
  userIcon,
  taskType,
  dateTime,
  fileSize,
  indexing,
  checkbox,
  assetCount,
  assetPreview,
  tagGroupsEdit,
  hoursInNumber,
  collectionPreview,
  createdByUserIcon,
}

export interface TableColumn {
  link?: string;
  header: string;
  class?: string;
  sticky?: boolean;
  filter?: boolean;
  visible?: boolean;
  sequence?: number;
  columnDef: string;
  showValue?: string;
  innerClass?: string;
  options?: AnyType[];
  dateColor?: boolean;
  showLength?: boolean;
  disableClick?: boolean;
  type?: TableColumnType;
  columnType?: ColumnType;
  filterSelected?: number;
  excludeClient?: string[];
}

export interface FilterTab {
  key: string;
  text: string;
  selectKey: string;
  filterId: string;
  visible?: boolean;
  unassignedFlag?: boolean;
  tabMenu?: boolean;
  tabType?: 'default' | 'dynamic';
  count?: number;
}

export interface MenuSections {
  section: MenuSettings[];
  sectionId: 'displayOptions' | 'tableOptions' | 'sortBy' | 'view' | 'timePeriod';
  sectionTitle: string;
}

export interface MenuSettings {
  menuId: string;
  menuTitle: string;
  disable?: boolean;
  showDivider?: boolean;
  selectedViewType?: ViewType;
  menuIcon?: string;
  iconClass?: string;
  svgIcon?: string;
  [key: string]: AnyType;
}

export interface TabMenuEvent {
  index: number;
  link: string;
  actionType?: string;
  isCustomTab?: boolean;
  previousTabInfo?: FilterTab;
  deleteTabFilterId?: string;
  tabType?: string;
}

export interface FilterObject {
  label: string;
  placeholder: string;
  name: string;
  input_type: string;
  multiple?: boolean;
  options: { id: AnyType; name: string }[];
  onSelectionChange?: (event: AnyType) => AnyType;
  defaultValue: AnyType;
}

export interface TaskDataKanban {
  status: string;
  type: string;
  name: string;
  deadline: string;
  members: { src: string; name: string; avatarSize: number; avatarFontSize: number }[];
}

export interface AssetTypeConfig {
  type: string;
  mimeTypes: { type: string; mimeType: string }[];
}

/**
 * Selection model, only id is used to identify the asset
 */
export interface SelectionAsset {
  _id: string;
}

/**
 * Asset model to be used on the UI side
 * This model extends the Asset model from the backend
 */
export interface AssetUIModel extends Asset {
  downloadPercent?: number;
  inProgress?: boolean;
  isAddedToBasket?: boolean;
  checked?: boolean;
  /**
   * Number of days remaining before the asset expires.
   * It could be negative if the asset has already expired.
   */
  assetExpiryDays?: number;
  mainUrl?: string;
  previewUrl?: string;
  imageUrl?: string;
  selectedVersion?: number;

  customFields?: Record<string, AnyType>;
  [key: string]: AnyType;

  /**
   * Type of relation in respect to the main asset (in relations tab)
   */
  relationType?: string;
}

export interface FileObject {
  file: File;
  key: string;
  name: string;
  uploading: boolean;
  uploaded: boolean;
  isExists?: boolean;
  isDuplicate?: boolean;
  previewUrl?: string;
  imageUrl?: string;
}

export interface AssetListFileObject extends FileObject {
  previewUrl: string;
  assetType: string;
  fallbackImg?: string;
}

export enum SCREEN_SIZE {
  XS,
  SM,
  MD,
  LG,
  XL,
  XXL,
}

export interface ProjectAssetsNFilesColumn {
  section: string;
  clusters: { type: string }[];
  assetList: AnyType[];
  count: number;
}

export enum TeamActions {
  'User_Added' = 'User_Added',
  'User_Removed' = 'User_Removed',
  'User_Set_Team_Leader' = 'User_Set_Team_Leader',
  'User_Removed_Team_Leader' = 'User_Removed_Team_Leader',
}

/**
 * This variable is for allianz specific form field options
 */
export const AdvertisingMaterialOptions = [
  'TV',
  'Online',
  'IG/FB-Story',
  'IG/FB-Post',
  'TW',
  'YT',
  'Pinterest',
  'TikTok',
  'OOH',
  'Kino',
  'Radio',
  'Podcast',
  'Sponsoring',
  'ABV',
];

export const AssetCategories = ['AI', 'CGI', 'Shooting', 'Stock'];

export const LufthansaContentOptions = [
  { _id: 'brand', name: 'Brand' },
  { _id: 'destination', name: 'Destination' },
  { _id: 'lhGroup', name: 'LH Group' },
  { _id: 'product', name: 'Product' },
];

export const LufthansaProductOptions = [
  { _id: 'eco', name: 'Eco' },
  { _id: 'premiumEco', name: 'Premium Eco' },
  { _id: 'businessClass', name: 'Business Class' },
  { _id: 'firstClass', name: 'First Class' },
];

export const CreativeAgencies = [
  { _id: 'ddbFrankfurt', name: 'DDB Frankfurt' },
  { _id: 'facitDigital', name: 'Facit digital' },
  { _id: 'facitResearch', name: 'Facit research' },
  { _id: 'jokeEvent', name: 'JOKE event' },
  { _id: 'jungVonMattCreators', name: 'Jung von Matt CREATORS' },
  { _id: 'lautVonLeise', name: 'Laut von leise' },
  { _id: 'mediaMonksGermany', name: 'Media.Monks Germany' },
  { _id: 'mediaplusAustria', name: 'Mediaplus Austria' },
  { _id: 'mediaplusBerlin', name: 'Mediaplus Berlin' },
  { _id: 'mediaplusChina', name: 'Mediaplus China' },
  { _id: 'mediaplusEngine', name: 'Mediaplus Engine' },
  { _id: 'mediaplusForward', name: 'Mediaplus Forward' },
  { _id: 'mediaplusFranceSas', name: 'Mediaplus France SAS' },
  { _id: 'mediaplusGeoIntelligence', name: 'Mediaplus Geo Intelligence' },
  { _id: 'mediaplusGermany', name: 'Mediaplus Germany' },
  { _id: 'mediaplusGlobalHealthGroup', name: 'Mediaplus Global Health Group' },
  { _id: 'mediaplusHamburg', name: 'Mediaplus Hamburg' },
  { _id: 'mediaplusIntelligenceRomania', name: 'Mediaplus Intelligence Romania' },
  { _id: 'mediaplusInternational', name: 'Mediaplus International' },
  { _id: 'mediaplusKoln', name: 'Mediaplus Köln' },
  { _id: 'mediaplusMedia1', name: 'Mediaplus Media 1' },
  { _id: 'mediaplusMedianestCroatia', name: 'Mediaplus Medianest Croatia' },
  { _id: 'mediaplusNetherland', name: 'Mediaplus Netherland' },
  { _id: 'mediaplusPerformance', name: 'Mediaplus Performance' },
  { _id: 'mediaplusRealtime', name: 'Mediaplus Realtime' },
  { _id: 'mediaplusSuisse', name: 'Mediaplus Suisse' },
  { _id: 'mediascaleKoln', name: 'Mediascale Köln' },
  { _id: 'nayokiMediaplus', name: 'Nayoki Mediaplus' },
  { _id: 'nowServiceplan', name: 'Now Serviceplan' },
  { _id: 'pereiraODell', name: "Pereira O'Dell	" },
  { _id: 'planNetAustria', name: 'Plan.Net Austria' },
  { _id: 'planNetBelgium', name: 'Plan.Net Belgium' },
  { _id: 'planNetBerlin', name: 'Plan.Net Berlin' },
  { _id: 'planNetCgn', name: 'Plan.Net CGN' },
  { _id: 'planNetGermany', name: 'Plan.Net Germany' },
  { _id: 'planNetHamburg', name: 'Plan.Net Hamburg' },
  { _id: 'planNetJourney', name: 'Plan.Net Journey' },
  { _id: 'planNetKoln', name: 'Plan.Net Köln' },
  { _id: 'planNetMedia2Ma', name: 'Plan.Net Media 2. MA' },
  { _id: 'planNetMunchen', name: 'Plan.Net München' },
  { _id: 'planNetNetherlands', name: 'Plan.Net Netherlands' },
  { _id: 'planNetNordics', name: 'Plan.Net Nordics' },
  { _id: 'planNetRadicalCrm', name: 'Plan.Net Radical CRM' },
  { _id: 'planNetSas', name: 'Plan.Net SAS' },
  { _id: 'planNetStudios', name: 'Plan.Net Studios' },
  { _id: 'planNetSuisse', name: 'Plan.Net Suisse' },
  { _id: 'planNetTechnestGermany', name: 'Plan.Net Technest Germany' },
  { _id: 'saintElmoSEntLa', name: "Saint Elmo's Ent. LA	" },
  { _id: 'saintElmoSEntertainment', name: "Saint Elmo's Entertainment	" },
  { _id: 'saintElmoSGermany', name: 'Saint Elmo’s Germany' },
  { _id: 'saintElmoSHamburg', name: "Saint Elmo's Hamburg	" },
  { _id: 'saintElmoSMunchen', name: "Saint Elmo's München	" },
  { _id: 'saintElmoSTourism', name: "Saint Elmo's Tourism	" },
  { _id: 'scholzAndFriendsHamburg', name: 'Scholz & Friends Hamburg' },
  { _id: 'serviceplanActivation', name: 'Serviceplan Activation' },
  { _id: 'serviceplanAiLab', name: 'Serviceplan AI Lab' },
  { _id: 'serviceplanAustria', name: 'Serviceplan Austria' },
  { _id: 'serviceplanBelgium', name: 'Serviceplan Belgium' },
  { _id: 'serviceplanBerlin', name: 'Serviceplan Berlin' },
  { _id: 'serviceplanBoostingHealth', name: 'Serviceplan BOOSTing Health' },
  { _id: 'serviceplanBubble', name: 'Serviceplan Bubble' },
  { _id: 'serviceplanChina', name: 'Serviceplan China' },
  { _id: 'serviceplanConsultingGroup', name: 'Serviceplan Consulting Group' },
  { _id: 'serviceplanContentHealth', name: 'Serviceplan Content Health' },
  { _id: 'serviceplanCulture', name: 'Serviceplan Culture' },
  { _id: 'serviceplanDocks', name: 'Serviceplan Docks' },
  { _id: 'serviceplanExperience', name: 'Serviceplan Experience' },
  { _id: 'serviceplanGroupNetherlands', name: 'Serviceplan Group Netherlands' },
  { _id: 'serviceplanGroupNorthAmerica', name: 'Serviceplan Group North America' },
  { _id: 'serviceplanGroupSe', name: 'Serviceplan Group SE' },
  { _id: 'serviceplanGroupUk', name: 'Serviceplan Group UK' },
  { _id: 'serviceplanHamburg', name: 'Serviceplan Hamburg' },
  { _id: 'serviceplanHealthAndLife', name: 'Serviceplan Health & Life' },
  { _id: 'serviceplanInternational', name: 'Serviceplan International' },
  { _id: 'serviceplanItaliaSrl', name: 'Serviceplan Italia SRL' },
  { _id: 'serviceplanKoln', name: 'Serviceplan Köln' },
  { _id: 'serviceplanKorea', name: 'Serviceplan Korea' },
  { _id: 'serviceplanMake', name: 'Serviceplan MAKE' },
  { _id: 'serviceplanMakeBenelux', name: 'Serviceplan MAKE Benelux' },
  { _id: 'serviceplanMiddleEast', name: 'Serviceplan Middle East' },
  { _id: 'serviceplanMunchen', name: 'Serviceplan München' },
  { _id: 'serviceplanNeo', name: 'Serviceplan Neo' },
  { _id: 'serviceplanNetherlands', name: 'Serviceplan Netherlands' },
  { _id: 'serviceplanNewYork', name: 'Serviceplan New York' },
  { _id: 'serviceplanNurnberg', name: 'Serviceplan Nürnberg' },
  { _id: 'serviceplanParis', name: 'Serviceplan Paris' },
  { _id: 'serviceplanPremiumServices', name: 'Serviceplan Premium Services' },
  { _id: 'serviceplanPublicRelations', name: 'Serviceplan Public Relations' },
  { _id: 'serviceplanStrategy', name: 'Serviceplan Strategy' },
  { _id: 'serviceplanSuisse', name: 'Serviceplan Suisse' },
  { _id: 'serviceplanUsa', name: 'Serviceplan USA' },
  { _id: 'strichpunkt', name: 'Strichpunkt' },
  { _id: 'tacsy', name: 'Tacsy' },
  { _id: 'theMarcomEngine', name: 'THE MARCOM ENGINE' },
  { _id: 'thjnkMunchen', name: 'thjnk München' },
  { _id: 'totalMediaplusUkLtd', name: 'Total Mediaplus UK Ltd.' },
  { _id: 'wefraMediaplus', name: 'Wefra Mediaplus' },
  { _id: 'wienNordServiceplan', name: 'Wien Nord Serviceplan' },
  { _id: 'wynkenBlynkenAndNod', name: 'Wynken Blynken & Nod' },
  { _id: 'youngAndRubicam', name: 'Young and Rubicam' },
];

export const AudioTypeOptions = ['R128', 'Web', 'Web-CLEAN'];

export const CustomRouteAgencyMap = [
  {
    route: 'metadata',
    agencies: [CustomClients.Allianz],
  },
];

export const TableActionObject = {
  columnDef: 'actions',
  type: TableColumnType.action,
  header: '',
  class: 'w-[5%] sticky-action-header',
  visible: true,
};

export interface DownloadAsset {
  name: string;
  url: string;
  total?: number;
  loaded?: number;
  inProgress: boolean;
  downloadPercent: number;
}

export type MultipleAgencyClientBrand = Record<number, CompanyBaseDataWithClients>;

export interface TagsDataConfig {
  searchText?: string; // for assets
  filters?: AnyType; // for assets
  callFrom?: 'child' | 'dialog' | 'dashboard' | 'sharing';
  selectedTags?: TagsWithAssetCount[];
  existingTags?: TagsWithAssetCount[];
}

export interface FooterNavigation {
  navLink: string[];
  navText: string;
}

export interface MetadataModel {
  formControlName: string;
  formFieldName: string;
  customField?: boolean;
  selected?: boolean;
  formFieldGroup?: MetadataModel[];
  hideField?: boolean;
}

export interface GroupList {
  lists: {
    _id: string;
    name: string;
    description?: string;
  }[];
  name: string;
}

export interface BuyoutRightsDialogs {
  uploadedAssets: AssetUIModel[];
  availableTitles: string[];
  singleAssetUpdate?: boolean;
  editMode?: boolean;
  index?: number;
  buyoutRightEditId?: string;
}

export interface GuidedTourConfig {
  skip: boolean;
}

export const AssetTypesLocalized = [
  { id: AssetTypeEnum.Image, name: $localize`:asset type:Image` },
  { id: AssetTypeEnum.Video, name: $localize`:asset type:Video` },
  { id: AssetTypeEnum.Audio, name: $localize`:asset type:Audio` },
  { id: AssetTypeEnum.PDF, name: $localize`:asset type:PDF` },
  { id: AssetTypeEnum.HtmlBanner, name: $localize`:asset type:HTML Banner` },
  { id: AssetTypeEnum.PPT, name: $localize`:asset type:Powerpoint presentation` },
  { id: AssetTypeEnum.ZIP, name: $localize`:asset type:ZIP` },
  { id: AssetTypeEnum.Other, name: $localize`:asset type:Other` },
];

export const AssetTypesImageVideo = [
  { id: AssetTypeEnum.Image, name: $localize`:asset type:Image` },
  { id: AssetTypeEnum.Video, name: $localize`:asset type:Video` },
];

export const FilterBubbleDateLabel: Record<string, string> = {
  createdDate_from: $localize`Created from`,
  createdDate_to: $localize`Created to`,
  expiryDate_from: $localize`Expiry from`,
  expiryDate_to: $localize`Expiry to`,
  plannedForDeletion_from: $localize`Planned for deletion to`,
  plannedForDeletion_to: $localize`Planned for deletion from`,
  publishedDate_from: $localize`Published from`,
  publishedDate_until: $localize`Published until`,
};

/**
 * Use only for displaying with the optionsLabel pipe - do not use to set status values
 */
export const AssetStatusLabels = [
  {
    _id: Asset.StatusEnum.Approved,
    name: $localize`Approved`,
  },
  {
    _id: Asset.StatusEnum.Archived,
    name: $localize`Archived`,
  },
  {
    _id: Asset.StatusEnum.Uploaded,
    name: $localize`Uploaded`,
  },
  {
    _id: Asset.StatusEnum.Failed,
    name: $localize`Failed`,
  },
  {
    _id: Asset.StatusEnum.Import,
    name: $localize`Import`,
  },
  {
    _id: Asset.StatusEnum.AwaitingApproval,
    name: $localize`AwaitingApproval`,
  },
  {
    _id: Asset.StatusEnum.Expired,
    name: $localize`Expired`,
  },
  {
    _id: Asset.StatusEnum.Created,
    name: $localize`Created`,
  },
  {
    _id: Asset.StatusEnum.Pending,
    name: $localize`Pending`,
  },
  {
    _id: Asset.StatusEnum.Rejected,
    name: $localize`Rejected`,
  },
  {
    _id: Asset.StatusEnum.Draft,
    name: $localize`Draft`,
  },
];
